import { SERVER_URL } from '../config.js';
import { storage } from './storage.js';
import { isValidEmail } from '../../core/utils/index.js';
import { showConfirm } from './UnifiedNotificationSystem.js';
import { getCurrentLanguage } from '../translations.js';
import { confirmEmailTypoIfNeeded } from './emailTypoConfirmation.js';

export async function apiCall(endpoint, method = 'GET', body = null) {
  const headers = { 'Content-Type': 'application/json' };
  const token = await window.tokenManager?.getValidAccessToken?.() ?? null;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${SERVER_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API call failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export let validationLock = false;
export let validationLockExpiry = 0;

const getToken = () => (window.tokenManager?.getAccessToken?.() ?? null);
const getUserData = () => {
  const data = storage.get('userData');
  try {
    return data || null;
  } catch {
    return null;
  }
};

const createSession = (token, user = null) => ({ access_token: token, user });
const getCacheKey = (token) => `token_validation_${token?.substring(0, 10)}`;

const getCachedValidation = (token) => {
  const now = Date.now();
  const cacheKey = getCacheKey(token);
  const cached = storage.get(cacheKey);
  const expiry = storage.get(`${cacheKey}_expiry`);

  if (cached && expiry && now < parseInt(expiry, 10)) {
    return cached;
  }
  return null;
};

export const getSession = async () => {
  try {
    if (typeof window !== 'undefined' && window.tokenManager) {
      const token = await window.tokenManager.getValidAccessToken();
      if (!token) return null;
      return createSession(token, getUserData());
    }

    const token = getToken();
    if (!token) return null;

    const now = Date.now();
    if (validationLock && now < validationLockExpiry) {
      const cached = getCachedValidation(token);
      return cached?.valid ? createSession(token, cached.user) : null;
    }

    const cached = getCachedValidation(token);
    if (cached?.valid) return createSession(token, cached.user);

    return null;
  } catch {
    return null;
  }
};

export const getUser = async () => {
  const session = await getSession();
  return session?.user || null;
};

export const signInWithGoogle = async () => {
  const url = `${SERVER_URL}/api/v1/auth/google/redirect`;
  window.location.href = url;
  return { url };
};

export const signInWithTwitter = async () => {
  const url = `${SERVER_URL}/api/v1/auth/twitter/redirect`;
  window.location.href = url;
  return { url };
};

export const signInWithDiscord = async () => {
  const url = `${SERVER_URL}/api/v1/auth/discord/redirect`;
  window.location.href = url;
  return { url };
};

export const signOut = async () => {
  try {
    if (typeof window !== 'undefined' && window.tokenManager) {
      try {
        await apiCall('/api/v1/auth/logout', 'POST');
      } catch (error) {
        console.warn('Logout API failed:', error);
      }

      // For complete logout, clear userData entirely (don't preserve preferences)
      window.tokenManager.constructor.clearUserData();
      await window.tokenManager.clearTokens();
      document.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { reason: 'User initiated logout' }
      }));
      return true;
    }

    await apiCall('/api/v1/auth/logout', 'POST');
    ['auth_token', 'manual_token', 'refresh_token'].forEach(key =>
      storage.remove(key)
    );
    // For complete logout, clear userData entirely
    window.tokenManager?.constructor?.clearUserData();
    return true;
  } catch (error) {
    throw error;
  }
};

export const refreshSession = async () => {
  try {
    if (typeof window !== 'undefined' && window.tokenManager) {
      const token = await window.tokenManager.refreshAccessToken();
      return token ? createSession(token) : null;
    }

    const refreshToken = storage.get('refresh_token');
    if (!refreshToken) return null;

    const data = await apiCall('/api/v1/auth/refresh', 'POST', { refresh_token: refreshToken });
    if (data?.access_token) {
      storage.set('auth_token', data.access_token);
      if (data.refresh_token) {
        storage.set('refresh_token', data.refresh_token);
      }
      return createSession(data.access_token);
    }
    return null;
  } catch {
    return null;
  }
};

export const setSession = async (accessToken, refreshToken = '') => {
  try {
    const userData = getUserData();
    
    if (typeof window !== 'undefined' && window.tokenManager) {
      window.tokenManager.setTokens(accessToken, refreshToken, userData);
      return createSession(accessToken);
    }

    storage.set('auth_token', accessToken);
    if (refreshToken) storage.set('refresh_token', refreshToken);

    const now = Date.now();
    validationLock = true;
    validationLockExpiry = now + 10000;

    const cacheKey = getCacheKey(accessToken);
    storage.set(cacheKey, {
      valid: true,
      user: userData || { id: 'temp', email: '', user_metadata: {} }
    });
    storage.set(`${cacheKey}_expiry`, (now + 5 * 60 * 1000).toString());
    
    validationLock = false;
    return createSession(accessToken);
  } catch (error) {
    validationLock = false;
    return null;
  }
};

export const isAuthenticated = async () => {
  const session = await getSession();
  return !!session;
};

const getEmailTypoTranslation = (key, replacements = {}) => {
  const lang = getCurrentLanguage?.() || 'en';
  const accountTranslations = window.translations?.[lang]?.account || window.translations?.en?.account || {};
  const typoTranslations = accountTranslations.email_typo || {};
  const keyMap = {
    message: 'message',
    useTypedEmail: 'use_typed_email',
    editEmail: 'edit_email',
  };

  const defaults = {
    message: 'Did you mean {suggestedEmail}? You entered {typedEmail}.',
    useTypedEmail: 'Use {typedEmail}',
    editEmail: 'Edit email',
  };

  const template = typoTranslations[keyMap[key]] || defaults[key] || '';
  return Object.entries(replacements).reduce(
    (text, [replacementKey, value]) => text.replaceAll(`{${replacementKey}}`, String(value)),
    template
  );
};

export const signInWithMagicLink = async (email) => {
  const normalizedEmail = typeof email === 'string' ? email.trim().toLowerCase() : '';
  if (!isValidEmail(normalizedEmail)) {
    throw new Error('Invalid email address');
  }

  const { shouldProceed } = await confirmEmailTypoIfNeeded({
    email: normalizedEmail,
    translate: getEmailTypoTranslation,
    confirm: showConfirm,
  });

  if (!shouldProceed) {
    throw new Error('EMAIL_TYPO_EDIT_REQUESTED');
  }

  const response = await apiCall('/api/v1/auth/magic', 'POST', { email: normalizedEmail });
  if (response.error) throw new Error(response.error);

  return { success: true, email: normalizedEmail };
};

export const getCurrentUserId = () => {
  const tokenManagerUserId = window.tokenManager?.getUserId?.();
  if (tokenManagerUserId) {
    return tokenManagerUserId;
  }

  try {
    const userData = storage.get('userData') || {};
    const storedUserId = userData?.id || userData?.user_id;
    if (storedUserId) {
      return storedUserId;
    }
  } catch {
    // Ignore storage read errors and continue to fallback sources.
  }

  const fallbackUserId = storage.get('userId');
  if (fallbackUserId) {
    return fallbackUserId;
  }

  return null;
};